/**
 * EmployerShell — chrome for the Employer CXO dashboard.
 *
 * A lean top-bar layout (no patient sidebar nav — the CXO surface is a
 * single analytics screen for now). Brand-palette chrome; the dashboard
 * content itself uses the emerald editorial idiom, matching the internal
 * patient screens.
 */

import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ShieldCheck } from 'lucide-react';
import { logout } from '@cardioq/api-client';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

export function EmployerShell({ children }: { children: React.ReactNode }) {
  const { logout: clearLocalAuth } = useAuth();
  const navigate = useNavigate();

  async function handleSignOut() {
    await logout();
    clearLocalAuth();
    navigate('/login', { replace: true });
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-brand-bg text-brand-slate-100">
      {/* Ambient brand-gradient glow — barely-there atmospheric tint */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-40"
        style={{
          backgroundImage:
            'radial-gradient(40% 50% at 88% -10%, rgba(34,195,237,0.10), transparent 55%), radial-gradient(40% 60% at 6% 110%, rgba(31,111,229,0.10), transparent 60%)',
        }}
      />

      {/* Sticky top bar */}
      <header className="sticky top-0 z-20 border-b border-brand-slate-800/60 bg-brand-bg/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-4 px-6 sm:px-8">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-slate-500">
              Employer&nbsp;CXO
            </p>
          </div>
          <div className="flex-1" />
          {/* Group-policy insurance view — same account, /insurance section */}
          <Link
            to="/insurance/employer"
            className="inline-flex items-center gap-2 rounded-full border border-brand-slate-700/60 bg-brand-slate-900/40 px-4 py-1.5 text-sm text-brand-slate-300 transition-colors hover:border-brand-slate-600 hover:bg-brand-slate-800/40 hover:text-brand-slate-100 focus-ring"
          >
            <ShieldCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Insurance</span>
          </Link>
          <ThemeToggle />
          <button
            type="button"
            onClick={handleSignOut}
            className="inline-flex items-center gap-2 rounded-full border border-brand-slate-700/60 bg-brand-slate-900/40 px-4 py-1.5 text-sm text-brand-slate-300 transition-colors hover:border-brand-slate-600 hover:bg-brand-slate-800/40 hover:text-brand-slate-100 focus-ring"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* Page content */}
      <main className="relative z-10 flex-1">
        <div className="mx-auto w-full max-w-6xl px-6 py-8 sm:px-8 sm:py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
